import {
  SupportedCountry,
  PhoneInputProps,
  isValidPhoneNumber,
} from "@brexhq/component-library";
import React from "react";
import {
  useForm,
  UseFormTrigger,
  UseControllerProps,
  useController,
} from "react-hook-form";

import {
  LeadRoutingEventType,
  ScriptLoadingStatus,
  EMPOWER_MULTI_STEP_LEAD_FORM,
  HOW_DID_YOU_HEAR_ABOUT_US_MAX_LENGTH,
  PLEASE_USE_WORK_EMAIL_MESSAGE,
  PLEASE_USE_A_VALID_WEBSITE_MESSAGE,
  INVALID_INPUT_MESSAGE,
} from "@brexhq/next/src/components/modules/FormMultistep/types";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import {
  LeadRoutingEvent,
  FieldDirtiedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { logThirdPartyScriptError } from "@brexhq/next/src/telemetry/logging";
import { Winda } from "@brexhq/next/src/utils/Winda";
import {
  QuestionSalesVolumeLeadForm,
  LeadSelfAttestedType,
} from "@brexhq/next/types/brex-api";

import { AnalyticsEvent, AnalyticsApi } from "../../../telemetry/types";
import isPersonalEmail from "../../../utils/isPersonalEmail";
import isValidEmail from "../../../utils/isValidEmail";
import isValidURL from "../../../utils/isValidURL";
import { localStorage } from "../../../utils/localStorage";

import { LeadFormOptions, FieldValues } from "./dynamicLeadFormController";

export const src = "https://js.chilipiper.com/marketing.js";

// 30sec timeout
export const SCRIPT_TIMEOUT = 30 * 1000;
export const MAX_LOADS = 3;

export const addScript = (
  handleLoad: (this: HTMLScriptElement, ev: Event) => any,
  handleError: (this: HTMLScriptElement, ev: ErrorEvent) => any,
) => {
  const scriptEl = document.createElement("script");
  scriptEl.setAttribute("src", src);
  scriptEl.setAttribute("type", "text/javascript");
  scriptEl.async = true;

  scriptEl.addEventListener("load", handleLoad);
  scriptEl.addEventListener("error", handleError);

  document.body.appendChild(scriptEl);

  return scriptEl;
};

export const getScript = () =>
  document.querySelectorAll(`script[src="${src}"]`);

export const removeScript = () => {
  getScript().forEach((el) => el?.remove());
};

export const handleChange = (
  trackEvent: (event: AnalyticsEvent) => void,
  setStatus: React.Dispatch<React.SetStateAction<ScriptLoadingState>>,
  shouldInitLeadRoutingScript: boolean,
  state: ScriptLoadingState,
) => {
  const { status, loadCount } = state;
  const trackLoadFailure = (message: string) => {
    logThirdPartyScriptError(message);
    trackEvent(
      new LeadRoutingEvent(
        "Chili Piper",
        LeadRoutingEventType.ROUTING_SCRIPT_LOADING_FAILED,
      ),
    );
  };

  const handleLoad = () => {
    // Handles false success caused by some ad blockers
    if (!window.ChiliPiper) {
      setStatus({ ...state, status: ScriptLoadingStatus.ERROR });
      trackLoadFailure("Chili Piper script loaded incorrectly");
      return;
    }

    setStatus({ ...state, status: ScriptLoadingStatus.SUCCESSFULLY_LOADED });
  };

  const handleError = (err: ErrorEvent) => {
    setStatus({ ...state, status: ScriptLoadingStatus.ERROR });
    trackLoadFailure(
      `Failed to load Chili Piper script due to error: ${err.message}`,
    );
  };

  if (!Winda.isPresent) {
    return;
  }

  const existing = getScript();
  if (existing.length > 0) {
    setStatus({
      ...state,
      status: ScriptLoadingStatus.SUCCESSFULLY_LOADED,
    });
    return;
  }

  if (!shouldInitLeadRoutingScript) {
    return;
  }

  setStatus({
    ...state,
    status: ScriptLoadingStatus.LOADING,
    loadCount: loadCount + 1,
  });

  // 10s should be too short for a human to finish the form and too long for the website to load the script.
  const timeoutId = setTimeout(() => {
    if (status !== ScriptLoadingStatus.SUCCESSFULLY_LOADED) {
      if (loadCount < MAX_LOADS) {
        removeScript();
      }
      trackLoadFailure("Failed to load Chili Piper script within timeout");
      setStatus({ ...state, status: ScriptLoadingStatus.TIMED_OUT });
    }
  }, SCRIPT_TIMEOUT);

  let scriptEl: HTMLScriptElement | undefined;
  if (status === ScriptLoadingStatus.LOADING) {
    scriptEl = addScript(handleLoad, handleError);
  }

  return () => {
    clearTimeout(timeoutId);
    scriptEl?.removeEventListener("load", handleLoad);
    scriptEl?.removeEventListener("error", handleError);
  };
};

interface ScriptLoadingState {
  status: ScriptLoadingStatus;
  loadCount: number;
}

export const useLeadRoutingScript = (shouldInitLeadRoutingScript = false) => {
  const { trackEvent } = useAnalytics();

  const [state, setState] = React.useState<ScriptLoadingState>({
    status: ScriptLoadingStatus.NOT_STARTED,
    loadCount: 0,
  });

  const { status } = state;

  React.useEffect(() => {
    return handleChange(
      trackEvent,
      setState,
      shouldInitLeadRoutingScript,
      state,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldInitLeadRoutingScript, status]);

  return {
    status,
    loadFailed:
      status === ScriptLoadingStatus.ERROR ||
      status === ScriptLoadingStatus.TIMED_OUT,
  };
};

const sanitizePattern = {
  value: /^[^<>=*]*$/,
  message: INVALID_INPUT_MESSAGE,
};

/**
 * Create and configure props for form fields.
 */
export const useLeadFormFields = (
  trackEvent: AnalyticsApi["trackEvent"],
  options: LeadFormOptions,
) => {
  const [country, setCountry] = React.useState<SupportedCountry>("US");

  const {
    control,
    handleSubmit,
    trigger: _trigger,
    formState,
    getValues,
    setValue,
  } = useForm<FieldValues>({
    mode: "onChange",
  });

  // Decorate form's trigger method to allow us to deactivate validation
  const trigger: UseFormTrigger<FieldValues> = async (...args) => {
    if (options.requiredFields) {
      return _trigger(...args);
    } else {
      return true;
    }
  };

  const baseRules: UseControllerProps["rules"] = {
    required: {
      message: "This field is required.",
      value: options.requiredFields,
    },
  };

  // Convenience method to make building the form less verbose
  const useField = <T extends keyof FieldValues>(
    label: string,
    name: T,
    rules: UseControllerProps["rules"] = baseRules,
  ) => {
    const ctrl = useController({
      control,
      name,
      rules,
    });

    return {
      label,
      name: ctrl.field.name,
      onBlur: ctrl.field.onBlur,
      onChange: ({ value }: { value: FieldValues[T] }) => {
        if (!ctrl.fieldState.isDirty) {
          trackEvent(
            new FieldDirtiedEvent(
              EMPOWER_MULTI_STEP_LEAD_FORM,
              ctrl.field.name,
            ),
          );
        }

        ctrl.field.onChange(value);
        /** Setting values in localStorage so users can continue on
         * will remove localstorage if value is empty (or erased)
         * just to make sure the form doesn't validate against ""
         */
        localStorage?.setItem(name, JSON.stringify(value));
        if (!value) {
          localStorage?.removeItem(name);
        }
      },
      value: ctrl.field.value as FieldValues[T],
      error: ctrl.fieldState.error?.message,
    };
  };

  const onCountryChange: PhoneInputProps["onCountryChange"] = ({ value }) => {
    // Reset number when country is changed. Validation behavior is a bit unstable otherwise
    setValue("mobileNumber", "");
    // Ensure we are showing validation errors if appropriate since changing country doesn't actually dirty the mobile number field
    trigger("mobileNumber");
    setCountry(value);
  };

  const fields = {
    firstName: useField("First name", "firstName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),
    lastName: useField("Last name", "lastName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),
    workEmail: useField("Work email", "workEmail", {
      ...baseRules,
      validate: () => {
        const value = getValues("workEmail");
        if (!isValidEmail(value)) return "Invalid email.";
        if (isPersonalEmail(value)) return PLEASE_USE_WORK_EMAIL_MESSAGE;
        return true;
      },
    }),
    mobileNumber: {
      ...useField("Mobile number", "mobileNumber", {
        required: "Mobile number is required.",
        validate: (value) => {
          if (
            !isValidPhoneNumber({
              value,
              country,
            })
          ) {
            return "Invalid phone number.";
          }
          return true;
        },
      }),
      error:
        formState.touchedFields.mobileNumber &&
        formState.errors.mobileNumber?.message,
      country,
      onCountryChange,
    },
    companyName: useField("Company name", "companyName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),

    website: useField("Website", "website", {
      ...baseRules,
      validate: (value) => {
        if (!isValidURL(value)) return PLEASE_USE_A_VALID_WEBSITE_MESSAGE;
        return true;
      },
    }),
    employeeCount: useField("Employee count", "employeeCount"),
    monthlyCreditSpend: useField(
      "Monthly credit card spend",
      "monthlyCreditSpend",
    ),

    monthlySales: {
      ...useField("Monthly sales", "monthlySales"),
      options: [
        {
          label: "<$250k",
          value: QuestionSalesVolumeLeadForm.UNDER_TWO_HUNDRED_FIFTY_K,
        },
        {
          label: "$250k+",
          value: QuestionSalesVolumeLeadForm.EQUAL_ABOVE_TWO_HUNDRED_FIFTY_K,
        },
      ],
    },

    isEcomm: {
      ...useField("Are you an e-commerce business?", "isEcomm"),
      options: [
        { label: "Yes", value: LeadSelfAttestedType.ECOMMERCE },
        { label: "No", value: LeadSelfAttestedType.OTHER },
      ],
    },

    fundingSource: useField("Primary source of funding", "fundingSource"),

    howDidYouHearAboutUs: useField(
      "How did you hear about us?",
      "howDidYouHearAboutUs",
      {
        ...baseRules,
        maxLength: {
          message: `This field must be less than ${HOW_DID_YOU_HEAR_ABOUT_US_MAX_LENGTH} characters long.`,
          value: HOW_DID_YOU_HEAR_ABOUT_US_MAX_LENGTH,
        },
        pattern: sanitizePattern,
      },
    ),
  };

  return {
    fields,
    country,
    onCountryChange,
    getValues,
    setValue,
    trigger,
    formState,
    handleSubmit,
  };
};
